import React, { useMemo, useState, useEffect } from 'react';

import Image from 'next/image';

import { useRouter } from 'next/router';

import { Button, Text } from '@BICE_Vida/components';

import { useSelector } from 'react-redux';
import { Badge } from '@BICE_Vida/scp-components';

import { ModalFull } from '@/designSystem/ModalFull';

import { CAT } from '@/utils/assets';
import componentsId from '@/utils/componentsId';
import { landingImageHero, landingImageHeroIcons, breakPointMd } from '@/utils/constants';
import { contactabilityFormTexts, landingText } from '@/utils/generalTexts';
import routes from '@/utils/routes';
import { clickActionsEvents, trackClick } from '@/utils/dataLayer/home';

import * as plansSelectors from '@/redux/plans/selectors';
import styles from './Hero.module.scss';
import RequirementsModal from './RequirementsModal';
import Features from './Features';
import Contactability from './Contactability';
import HeroMaxAgeCard from './HeroMaxAgeCard';
import { AvailablePlans as AvailablePlansType } from '@/src/types/availablePlans';

const Hero = () => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [isMobile, setIsMobile] = useState(true);

  const btnEstimateClick = () => {
    router.push(routes.wizardPlan);
    trackClick({ action: clickActionsEvents.hireBody });
  };

  const availablePlans = useSelector(plansSelectors.getAvailablePlans) as AvailablePlansType;

  const plansIncludePreexistingConditions = useMemo(
    () => availablePlans.filter((card) => card.base !== null && card.hidePlan !== true),
    [availablePlans]
  );

  const btnContactExecutiveClick = () => {
    trackClick({ action: clickActionsEvents.contactExecutiveOpen });
    setIsContactModalOpen(true);
  };

  const asideContent = !isMobile ? (
    <div className={styles.aside_content}>
      <Image src={CAT.CAT_THINKING_ROUND_YELLOW} alt="Gato pensando" width={128} height={128} />
      <div className={styles.aside_text_container}>
        <Text color="white" tag="h1" className={styles.aside_title_text}>
          {contactabilityFormTexts.asideTitle}
        </Text>
        <Text color="white" tag="p" className={styles.aside_body_text}>
          {contactabilityFormTexts.asideSubtitle}
        </Text>
      </div>
    </div>
  ) : (
    <div className={styles.aside_text_container}>
      <Text color="white" tag="h1" className={styles.aside_title_text}>
        {contactabilityFormTexts.asideTitle}
      </Text>
      <Text color="white" tag="p" className={styles.aside_body_text}>
        {contactabilityFormTexts.asideSubtitle}
      </Text>
    </div>
  );

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < breakPointMd);
    };

    window.addEventListener('resize', onResize);
    onResize();

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    const id =
      Object.keys(routes).find((key: string) => (routes[key] === router.pathname ? key : '')) || '';
    setIdentifier(id);
  }, []);

  return (
    <section className={styles.hero_section} id={componentsId.landing.hero.section}>
      <RequirementsModal isOpen={isOpen} setIsOpen={setIsOpen} />
      <ModalFull
        id="contact-executive-modal"
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        customClass={styles.modal_custom}
        asideChild={asideContent}
      >
        <div className={styles.modal_body_content}>
          <Contactability onClose={() => setIsContactModalOpen(false)} identifier={identifier} />{' '}
        </div>
      </ModalFull>
      <div>
        <div className={styles.wrap}>
          <div className={styles.description_section}>
            <Badge
              className={styles.hero_badge}
              text="Seguro Pyme"
              colorBg="#FDEA7A"
              colorText="#775A06"
            />
            <Text tag="h1" className={styles.hero_title}>
              {landingText.hero.title}
            </Text>
            <div className={styles.hero_bullet_points_container}>
              {landingText.hero.bulletPoints.map(
                (
                  bp: {
                    icon: string;
                    text: (onClick?: () => void) => React.ReactNode;
                    hasOnclickFunctionality: boolean;
                  },
                  index: number
                ) => (
                  <div key={'bullet' + index} className={styles.hero_bullet_points_item}>
                    <Image
                      className={styles.hero_bullet_point_icon}
                      src={bp.icon}
                      alt="ícono de check"
                      height={32}
                      width={24}
                    />
                    <Text tag="p" className={styles.hero_bullet_point_text}>
                      {bp.text(bp.hasOnclickFunctionality ? () => setIsOpen(true) : undefined)}
                    </Text>
                  </div>
                )
              )}
            </div>
            <HeroMaxAgeCard />
            <div className={styles.hero_button_container}>
              <Button
                id={`${identifier}_header_btn_contact_executive`}
                text={landingText.hero.buttonContactExecutive}
                size="large"
                onClick={btnContactExecutiveClick}
                aria-label={landingText.hero.buttonContactExecutive}
              />
              <Button
                className={styles.hero_btn_contact_quote}
                id={componentsId.landingHeroBtnPrices}
                text={landingText.hero.button}
                size="large"
                onClick={btnEstimateClick}
                aria-label={landingText.hero.button}
                variant="outline"
              />
            </div>
          </div>
          <div className={styles.hero_imagen_container}>
            <div className={styles.hero_icons_container}>
              {landingImageHeroIcons.map((icon, index) => (
                <Image key={index} src={icon.src} alt={icon.alt} width={64} height={64} />
              ))}
            </div>
            <div className={styles.hero_image}>
              {landingImageHero.map((item, index) => (
                <Image
                  priority={index === 0}
                  key={`${item.alt}_${index}`}
                  quality={60}
                  src={item.src}
                  alt={item.alt}
                  width={item.width}
                  height={item.height}
                />
              ))}
            </div>
          </div>
        </div>
        <div />
      </div>
      <Features availablePlans={plansIncludePreexistingConditions.length} />
    </section>
  );
};

export default Hero;
